import { ref, onBeforeUnmount } from 'vue';

// Assistente de IA — atualização periódica que sabe parar sozinha.
//
// A tela da base de conhecimento precisa se atualizar enquanto há documento sendo processado, e
// SÓ enquanto houver. Um intervalo que fica rodando para sempre é um pedido de recurso a cada
// cinco segundos, por horas, na aba que a pessoa esqueceu aberta.
//
// Três coisas vieram da revisão (AIA-07, C4):
// - a tarefa devolve `{ continuar, ok }`: quando a chamada FALHA, a atualização para e avisa,
//   em vez de bater na API a cada 5 s com a sessão expirada;
// - cada ciclo tem um número; resposta de ciclo antigo que chega atrasada é descartada, e não
//   sobrescreve a lista que a pessoa está vendo;
// - quem usa decide o que é "há motivo para continuar" — o filtro da tela não entra nessa conta.
export function usePolling(tarefa, { intervalo = 5000, aoFalhar = null } = {}) {
  const rodando = ref(false);
  const falhou = ref(false);
  let temporizador = null;
  let ciclo = 0;

  const parar = () => {
    if (temporizador) clearTimeout(temporizador);
    temporizador = null;
    rodando.value = false;
  };

  // "Este ciclo ainda vale?" — entregue À TAREFA, porque é ela que grava.
  //
  // A conferência daqui de baixo acontece DEPOIS que a tarefa já devolveu, e a tarefa já gravou
  // a lista antes disso: conferir depois não desfazia nada (AIA-08, C2).
  const aindaEhOCiclo = meuCiclo => () => meuCiclo === ciclo && rodando.value;

  const agendar = () => {
    temporizador = setTimeout(async () => {
      const meuCiclo = ciclo;
      const resultado = await tarefa(meuCiclo, aindaEhOCiclo(meuCiclo));

      // outro ciclo começou enquanto este esperava: o resultado deste não vale mais
      if (meuCiclo !== ciclo || !rodando.value) return;

      const { continuar = false, ok = true } =
        typeof resultado === 'object' && resultado !== null
          ? resultado
          : { continuar: resultado };

      if (!ok) {
        falhou.value = true;
        if (aoFalhar) aoFalhar();
        parar();
        return;
      }

      if (continuar) agendar();
      else parar();
    }, intervalo);
  };

  // Só começa se houver motivo; chamar de novo enquanto roda não duplica o temporizador.
  const comecar = () => {
    if (rodando.value) return;

    ciclo += 1;
    falhou.value = false;
    rodando.value = true;
    agendar();
  };

  // Trocar o filtro ou a página invalida o ciclo em curso: a resposta que já estiver voltando
  // não pode mais gravar nada.
  //
  // `ciclo` só crescia dentro de `comecar()`, que sai cedo quando já está rodando — ou seja,
  // enquanto a atualização periódica estivesse ligada, NADA invalidava ciclo nenhum, e a
  // resposta do pedido de "Todas" voltava por cima da lista filtrada por "Erro" (AIA-08, C2).
  // Quem invalida ASSUME A RESPONSABILIDADE DE REAGENDAR.
  //
  // Só aumentar o número era uma regressão grave: o callback do ciclo em voo voltava, via que já
  // não era o atual, e saía sem agendar e sem parar. `rodando` ficava `true` sem temporizador
  // nenhum, e a partir dali `comecar()` saía cedo PARA SEMPRE — a atualização automática morria,
  // o botão "Atualizar" não religava nada, e nenhum aviso aparecia (AIA-09, C1).
  const invalidar = () => {
    ciclo += 1;
    // parado continua parado: invalidar não é ligar
    if (!rodando.value) return;

    if (temporizador) clearTimeout(temporizador);
    agendar();
  };

  onBeforeUnmount(parar);

  return { rodando, falhou, comecar, parar, invalidar };
}
